import { useState } from "react";
import { Toaster, toast } from "sonner";
import { Mail, MapPin, Clock } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/contexts/AuthContext";

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", { name, email, message });
      setDone(true);
      toast.success("Message received. We'll reply within 24h.");
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#ffffff] min-h-screen">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="overline">Get in touch</p>
          <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] mt-4">
            Speak to <span className="italic text-[#000000]">a person.</span>
          </h1>
          <p className="mt-6 text-[#000000]/70 leading-relaxed max-w-md">
            Bespoke enquiries, sizing, trade-in valuations, shipping questions
            or simply to talk leather — write to us. Our team answers within 24
            hours, Monday through Saturday.
          </p>

          <dl className="mt-10 space-y-6 text-sm">
            <div data-testid="contact-email-block">
              <dt className="overline flex items-center gap-2">
                <Mail size={14} strokeWidth={1.5} className="text-[#000000]" />
                Email
              </dt>
              <dd className="mt-1">
                <a
                  href="mailto:hello@boscage.com"
                  className="text-[#000000] hover:text-[#000000] transition-colors"
                >
                  hello@boscage.com
                </a>
              </dd>
            </div>

            <div data-testid="contact-address-block">
              <dt className="overline flex items-center gap-2">
                <MapPin size={14} strokeWidth={1.5} className="text-[#000000]" />
                Address
              </dt>
              <dd className="mt-1 text-[#000000]/80 leading-relaxed not-italic">
                BOSCAGE Elegance
                <br />
                5856 Mount Vernon Dr, VA,
                <br />
                Alexandria, VA 22303
                <br />
                United States
              </dd>
            </div>

            <div data-testid="contact-hours-block">
              <dt className="overline flex items-center gap-2">
                <Clock size={14} strokeWidth={1.5} className="text-[#000000]" />
                Hours
              </dt>
              <dd className="mt-1 text-[#000000]/80">
                Monday – Saturday  ·  09:00 – 19:00 EST
              </dd>
            </div>
          </dl>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-[#f5f5f5] p-8 md:p-10 space-y-4"
        >
          <input
            data-testid="contact-name"
            className="input-light bg-[#ffffff]"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            data-testid="contact-email"
            type="email"
            className="input-light bg-[#ffffff]"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <textarea
            data-testid="contact-message"
            className="input-light bg-[#ffffff] min-h-[180px]"
            placeholder="Your message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          />
          <button
            data-testid="contact-submit"
            disabled={submitting}
            className="btn-primary w-full"
          >
            {submitting ? "Sending…" : "Send message"}
          </button>
          {done && (
            <p
              data-testid="contact-success"
              className="text-[10px] tracking-[0.22em] uppercase text-[#000000]"
            >
              Received · we&apos;ll be in touch within 24h.
            </p>
          )}
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#000000]/55">
            Or write directly to hello@boscage.com
          </p>
        </form>
      </section>

      <Toaster theme="dark" position="top-center" />
    </div>
  );
}
